import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  VehicleModificationApi,
  type CreateVehicleModificationDto,
  type UpdateVehicleModificationDto,
} from '@/shared/api/vehicleModifications'
import { useAuth } from '@/modules/auth'

const modificationsKey = (vehicleId: string) => ['vehicleModifications', vehicleId] as const
const statsKey = (vehicleId: string) => ['vehicleModificationStats', vehicleId] as const

/** A vehicle's build list, for its owner or a visitor. The server hides prices from visitors. */
export function useVehicleModifications(vehicleId: string) {
  const { getAccessToken, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const api = useMemo(() => new VehicleModificationApi(getAccessToken), [getAccessToken])

  const {
    data: modifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: modificationsKey(vehicleId),
    queryFn: () => api.getVehicleModifications(vehicleId),
    enabled: isAuthenticated && !!vehicleId,
  })

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: modificationsKey(vehicleId) }),
      queryClient.invalidateQueries({ queryKey: statsKey(vehicleId) }),
    ])

  const createMutation = useMutation({
    mutationFn: (data: CreateVehicleModificationDto) => api.createModification(data),
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ modificationId, data }: { modificationId: string; data: UpdateVehicleModificationDto }) =>
      api.updateModification(modificationId, data),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (modificationId: string) => api.deleteModification(modificationId),
    onSuccess: invalidate,
  })

  return {
    modifications,
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetch,
    createModification: createMutation.mutateAsync,
    updateModification: (modificationId: string, data: UpdateVehicleModificationDto) =>
      updateMutation.mutateAsync({ modificationId, data }),
    deleteModification: deleteMutation.mutateAsync,
  }
}

/** Build list totals. `totalInvestment` is only present for the vehicle's owner. */
export function useVehicleModificationStats(vehicleId: string) {
  const { getAccessToken, isAuthenticated } = useAuth()
  const api = useMemo(() => new VehicleModificationApi(getAccessToken), [getAccessToken])

  const { data: stats = null, isLoading } = useQuery({
    queryKey: statsKey(vehicleId),
    queryFn: () => api.getModificationStats(vehicleId),
    enabled: isAuthenticated && !!vehicleId,
  })

  return { stats, isLoading }
}
